import posthog from "posthog-js";

/**
 * PostHog Analytics Wrapper
 * Only captures events if PostHog is initialized (prod with env vars on /v2 routes)
 */

// Check if PostHog is ready to capture events
const isPostHogReady = (): boolean => {
  try {
    // PostHog sets __loaded when fully initialized
    return typeof window !== "undefined" && posthog && (posthog as any).__loaded === true;
  } catch {
    return false;
  }
};

// Safe capture wrapper
const capture = (eventName: string, properties?: Record<string, unknown>) => {
  if (isPostHogReady()) {
    posthog.capture(eventName, properties);
  }
};

// Identify user (call after authentication)
export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (isPostHogReady()) {
    posthog.identify(userId, traits);
  }
};

// Reset user on logout
export const resetUser = () => {
  if (isPostHogReady()) {
    posthog.reset();
  }
};

// Opt-in to capturing (call when user authenticates)
export const optInCapturing = () => {
  if (typeof window !== "undefined" && posthog) {
    posthog.opt_in_capturing();
  }
};

// Opt-out of capturing (call when user logs out)
export const optOutCapturing = () => {
  if (typeof window !== "undefined" && posthog) {
    posthog.opt_out_capturing();
  }
};

// ============================================
// Workflow Events
// ============================================

export const trackWorkflowCreated = (workflow: { id: string; type: string; nodeCount: number }) => {
  capture("workflow_created", {
    workflow_id: workflow.id,
    workflow_type: workflow.type,
    node_count: workflow.nodeCount,
  });
};

export const trackWorkflowPublished = (workflowId: string) => {
  capture("workflow_published", {
    workflow_id: workflowId,
  });
};

export const trackWorkflowUnpublished = (workflowId: string) => {
  capture("workflow_unpublished", {
    workflow_id: workflowId,
  });
};

export const trackWorkflowArchived = (workflowId: string) => {
  capture("workflow_archived", {
    workflow_id: workflowId,
  });
};

export const trackWorkflowRun = (workflowId: string, runId: string, mode: "live" | "test") => {
  capture("workflow_run_started", {
    workflow_id: workflowId,
    run_id: runId,
    mode,
  });
};

export const trackWorkflowSaved = (workflowId: string) => {
  capture("workflow_saved", {
    workflow_id: workflowId,
  });
};

// ============================================
// Workflow Builder Events
// ============================================

export const trackNodeAdded = (nodeType: string, nodeId: string, workflowNodeCount: number) => {
  capture("workflow_node_added", {
    node_type: nodeType,
    node_id: nodeId,
    total_nodes: workflowNodeCount,
  });
};

export const trackNodeDeleted = (nodeType: string, nodeId: string) => {
  capture("workflow_node_deleted", {
    node_type: nodeType,
    node_id: nodeId,
  });
};

export const trackAIWorkflowGenerated = (sessionId: string, nodeCount: number) => {
  capture("ai_workflow_generated", {
    session_id: sessionId,
    node_count: nodeCount,
  });
};

export const trackAIChatMessage = (sessionId: string, messageLength: number) => {
  capture("ai_chat_message_sent", {
    session_id: sessionId,
    message_length: messageLength,
  });
};

// ============================================
// Connector Events
// ============================================

export const trackConnectorCreated = (connector: { id: string; type: string }) => {
  capture("connector_created", {
    connector_id: connector.id,
    connector_type: connector.type,
  });
};

export const trackConnectorDeleted = (connectorId: string, connectorType: string) => {
  capture("connector_deleted", {
    connector_id: connectorId,
    connector_type: connectorType,
  });
};

// ============================================
// Template Events
// ============================================

export const trackTemplateViewed = (templateId: string, templateName?: string) => {
  capture("template_viewed", {
    template_id: templateId,
    template_name: templateName,
  });
};

export const trackTemplateUsed = (templateId: string, templateName?: string) => {
  capture("template_used", {
    template_id: templateId,
    template_name: templateName,
  });
};

// ============================================
// Navigation Events
// ============================================

export const trackPageView = (pageName: string, properties?: Record<string, unknown>) => {
  capture("page_viewed", {
    page_name: pageName,
    ...properties,
  });
};

export const trackSearchUsed = (searchTerm: string, resultCount: number, context: string) => {
  capture("search_used", {
    search_term: searchTerm,
    result_count: resultCount,
    context,
  });
};

// ============================================
// Organization Events
// ============================================

export const trackOrgSwitched = (orgId: string) => {
  capture("organization_switched", {
    org_id: orgId,
  });
};

// ============================================
// Onboarding Events
// ============================================

export const trackOnboardingStarted = (properties: { hasExistingOrg: boolean; startStep: number }) => {
  capture("onboarding_started", {
    has_existing_org: properties.hasExistingOrg,
    start_step: properties.startStep,
  });
};

export const trackOnboardingOrgCreated = (orgId: string) => {
  capture("onboarding_org_created", {
    org_id: orgId,
  });
};

export const trackOnboardingGoalsSelected = (properties: { goalCount: number; primaryGoalId: string }) => {
  capture("onboarding_goals_selected", {
    goal_count: properties.goalCount,
    primary_goal_id: properties.primaryGoalId,
  });
};

export const trackOnboardingCompleted = (exitPath: "completed" | "skipped") => {
  capture("onboarding_completed", {
    exit_path: exitPath,
  });
};

// ============================================
// Wallet Events
// ============================================

export const trackWalletFunded = (walletId: string, amount: string, token: string) => {
  capture("wallet_funded", {
    wallet_id: walletId,
    amount,
    token,
  });
};

export const trackWalletCreated = (properties: { walletId: string; chainFamily?: string }) => {
  capture("wallet_created", {
    wallet_id: properties.walletId,
    chain_family: properties.chainFamily,
  });
};

export const trackWalletCreateError = (properties: { errorMessage: string; chainFamily?: string }) => {
  capture("wallet_create_error", {
    error_message: properties.errorMessage,
    chain_family: properties.chainFamily,
  });
};

export const trackWalletDeleted = (walletId: string) => {
  capture("wallet_deleted", { wallet_id: walletId });
};

export const trackWalletDeleteError = (properties: { walletId: string; errorMessage: string }) => {
  capture("wallet_delete_error", {
    wallet_id: properties.walletId,
    error_message: properties.errorMessage,
  });
};

export const trackWalletSendCompleted = (properties: {
  walletId: string;
  chainId: number;
  tokenAddress: string;
  transactionHash: string;
}) => {
  capture("wallet_send_completed", {
    wallet_id: properties.walletId,
    chain_id: properties.chainId,
    token_address: properties.tokenAddress,
    transaction_hash: properties.transactionHash,
  });
};

export const trackWalletSendError = (properties: {
  walletId: string;
  chainId?: number;
  tokenAddress?: string;
  errorMessage: string;
}) => {
  capture("wallet_send_error", {
    wallet_id: properties.walletId,
    chain_id: properties.chainId,
    token_address: properties.tokenAddress,
    error_message: properties.errorMessage,
  });
};

export const trackWalletImportCompleted = (walletAddress?: string) => {
  capture("wallet_import_completed", { wallet_address: walletAddress });
};

export const trackWalletImportError = (errorMessage: string) => {
  capture("wallet_import_error", { error_message: errorMessage });
};

export const trackWalletExportCompleted = (walletAddress?: string) => {
  capture("wallet_export_completed", { wallet_address: walletAddress });
};

export const trackWalletExportError = (properties: { walletAddress?: string; errorMessage: string }) => {
  capture("wallet_export_error", {
    wallet_address: properties.walletAddress,
    error_message: properties.errorMessage,
  });
};

// ============================================
// AI Chat Stream Events
// ============================================

export const trackAIChatStreamError = (properties: {
  errorType: "idle_timeout" | "network_error" | "server_error" | "max_duration" | "unknown";
  errorMessage: string;
  conversationId?: string;
  requestId?: string;
  streamDurationMs?: number;
}) => {
  capture("ai_chat_stream_error", {
    error_type: properties.errorType,
    error_message: properties.errorMessage,
    conversation_id: properties.conversationId,
    request_id: properties.requestId,
    stream_duration_ms: properties.streamDurationMs,
  });
};

export const trackAIChatStreamReconnect = (properties: {
  attempt: number;
  outcome: "success" | "failure" | "abort";
  conversationId?: string;
  requestId?: string;
}) => {
  capture("ai_chat_stream_reconnect", {
    attempt: properties.attempt,
    outcome: properties.outcome,
    conversation_id: properties.conversationId,
    request_id: properties.requestId,
  });
};

// ============================================
// Error Events
// ============================================

export const trackError = (errorType: string, errorMessage: string, context?: string) => {
  capture("error_occurred", {
    error_type: errorType,
    error_message: errorMessage,
    context,
  });
};
